// Argus — Helpers pentru File Explorer (cache server + refresh dir_listing).
// Cache GET `/api/v1/agents/{id}/fs?parent={path}` returnează entries din agent_fs_entries.
// Refresh = emit comandă `dir_listing` la agent → upload artifact → server post-process refresh cache.

import { apiFetch } from './api';
import { wsClient, WsCommandFailureError } from './wsClient';
import { iconCache } from './iconCache';

export interface FsEntry {
  readonly name: string;
  readonly isDir: boolean;
  readonly size: number | null;
  readonly modifiedAt: string | null;
  readonly isHidden: boolean;
  readonly isSystem: boolean;
  readonly volumeLabel: string | null;
  readonly driveType: string | null;
  readonly freeBytes: number | null;
  readonly totalBytes: number | null;
  readonly iconHash: string | null;
  readonly lastSyncedAt: string;
}

/**
 * Build URL pentru iconul real Windows extras de agent (per-agent unique).
 * Browser cache HTTP automat (Cache-Control max-age=86400 + ETag) — nu re-fetch.
 */
export function iconUrl(agentId: string, iconHash: string): string {
  return `/api/v1/agents/${agentId}/icons/${iconHash}`;
}

/**
 * Format display name Windows-style pentru drives (parent='').
 * Exemple: "Local Disk (C:)", "T7 Shield (D:)", "OBS Network (Z:)".
 * Pentru non-drives sau entries fără driveType → returnează name as-is.
 */
export function formatEntryDisplayName(entry: FsEntry): string {
  if (entry.driveType === null) return entry.name;
  // entry.name e "C:\" — extragem letter.
  const letterMatch = entry.name.match(/^([A-Za-z]):\\?$/);
  const letter = letterMatch?.[1] ?? entry.name.replace(/[\\\/]/g, '');
  let label = entry.volumeLabel?.trim() ?? '';
  if (label === '') {
    if (entry.driveType === 'fixed') label = 'Local Disk';
    else if (entry.driveType === 'removable') label = 'Removable Disk';
    else if (entry.driveType === 'cdrom') label = 'CD/DVD Drive';
    else if (entry.driveType === 'network') label = 'Network Drive';
    else label = 'Drive';
  }
  return `${label} (${letter}:)`;
}

export interface FsEntriesResponse {
  readonly parentPath: string;
  readonly items: readonly FsEntry[];
}

export async function fetchFsEntries(
  agentId: string,
  parentPath: string
): Promise<FsEntriesResponse> {
  const url = `/api/v1/agents/${agentId}/fs?parent=${encodeURIComponent(parentPath)}`;
  const result = await apiFetch<FsEntriesResponse>(url);
  return result.data;
}

export interface RefreshOptions {
  readonly maxDepth?: number;
}

export class PathNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PathNotFoundError';
  }
}

/**
 * Mapează `DirListingEntry` din WS payload (camelCase) la `FsEntry` (frontend canonical
 * type aliniat cu schema DB). Resolve iconHash din `icons` catalog (bloc ws-icons-inline).
 */
function mapWsEntry(
  e: import('./wsClient').DirListingEntry,
  icons: Record<string, import('./wsClient').IconBundle> | undefined
): FsEntry {
  const iconKey = (e as { iconKey?: string | null }).iconKey ?? null;
  const iconHash = iconKey && icons ? icons[iconKey]?.hash ?? null : null;
  return {
    name: e.name,
    isDir: e.type === 'dir',
    size: e.size,
    modifiedAt: e.modifiedAt === '' ? null : e.modifiedAt,
    isHidden: e.isHidden,
    isSystem: false,
    volumeLabel: e.volumeLabel ?? null,
    driveType: normalizeDriveType(e.driveType ?? null),
    freeBytes: e.freeBytes ?? null,
    totalBytes: e.totalBytes ?? null,
    iconHash,
    lastSyncedAt: new Date().toISOString(),
  };
}

function normalizeDriveType(raw: string | null): string | null {
  if (raw === null) return null;
  // Agent emite 'fixed'|'removable'|'cdrom'|'network' lowercase; DB cache stochează
  // 'Local'|'Removable'|'CDRom'|'Network' (capitalized via PHP). Aliniem la format canonical.
  switch (raw.toLowerCase()) {
    case 'fixed': return 'Local';
    case 'removable': return 'Removable';
    case 'cdrom': return 'CDRom';
    case 'network': return 'Network';
    default: return raw;
  }
}

/**
 * Refresh via WS direct — singura cale când agent online după cleanup 2026-05-05.
 * Returnează entries direct din WS payload (NU mai facem REST GET).
 *
 * Caller folosește entries returnate; pe failure cu AGENT_OFFLINE poate face
 * fallback la `fetchFsEntries` (REST din DB cache).
 */
export async function refreshFsEntriesViaWs(
  agentId: string,
  parentPath: string,
  opts: RefreshOptions = {}
): Promise<FsEntry[]> {
  try {
    const result = await wsClient.requestDirListing(agentId, parentPath, {
      recursive: false,
      maxDepth: opts.maxDepth ?? 1,
    });
    // Aplică icons catalog la cache memory ÎNAINTE de map (mapWsEntry rezolvă iconHash din catalog).
    iconCache.applyCatalog(result.icons);
    return result.entries.map((e) => mapWsEntry(e, result.icons));
  } catch (err) {
    if (err instanceof WsCommandFailureError) {
      if (err.errorCode === 'PATH_NOT_FOUND' || err.errorCode === 'PATH_NOT_DIRECTORY') {
        throw new PathNotFoundError(err.message);
      }
    }
    throw err;
  }
}

/**
 * Calcul parent path pentru navigare „up" — taie ultimul segment Windows-style.
 * `C:\Users\iulic\Documents` → `C:\Users\iulic`. `C:\` → `C:\`.
 */
export function parentOfPath(path: string): string {
  if (typeof path !== 'string' || path === '') return '';
  if (/^[A-Za-z]:\\?$/.test(path)) {
    return path.endsWith('\\') ? path : `${path}\\`;
  }
  const trimmed = path.endsWith('\\') ? path.slice(0, -1) : path;
  const idx = trimmed.lastIndexOf('\\');
  if (idx <= 2) {
    return trimmed.substring(0, 3);
  }
  return trimmed.substring(0, idx);
}

/**
 * Concatenează parent + name într-un path Windows valid.
 * Idempotent pe trailing separator.
 */
export function joinPath(parent: string, name: string): string {
  // Drives root case: parent='' + name='C:\\' (absolute deja) → returnează name as-is.
  if (parent === '') return name;
  if (parent.endsWith('\\')) return parent + name;
  return `${parent}\\${name}`;
}

export function pathSegments(path: string): readonly string[] {
  if (path === '' || path === '\\') return [];
  return path.split('\\').filter((s) => s !== '');
}
